export const TURKEY_TIME_ZONE = 'Europe/Istanbul';

export function turkeyDate(value = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TURKEY_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(value);
  const part = (name) => parts.find((item) => item.type === name).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function shiftDate(iso, days) {
  const value = new Date(`${iso}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function weekStart(iso) {
  const day = new Date(`${iso}T12:00:00Z`).getUTCDay();
  return shiftDate(iso, -((day + 6) % 7));
}

export function studyWindow(entries, today, days) {
  const start = shiftDate(today, 1 - days);
  const dates = Array.from({ length: days }, (_, i) => shiftDate(start, i));
  const byDay = new Map();
  entries.filter((entry) => entry.date >= start && entry.date <= today).forEach((entry) => {
    const previous = byDay.get(entry.date) || { questions: 0, pages: 0 };
    byDay.set(entry.date, { questions: previous.questions + Number(entry.questions || 0), pages: previous.pages + Number(entry.pages || 0) });
  });
  const questions = dates.map((date) => byDay.has(date) ? byDay.get(date).questions : null);
  const pages = dates.map((date) => byDay.has(date) ? byDay.get(date).pages : null);
  const totalQuestions = questions.reduce((sum, value) => sum + (value || 0), 0);
  const totalPages = pages.reduce((sum, value) => sum + (value || 0), 0);
  return { dates, questions, pages, recordedDays: byDay.size, totalQuestions, totalPages,
    avgQuestions: byDay.size ? totalQuestions / byDay.size : null,
    avgPages: byDay.size ? totalPages / byDay.size : null };
}

export function studyComparison(entries, today, days = 7) {
  const empty = { questionsPct: null, pagesPct: null, complete: false };
  if (!Array.isArray(entries) || !today) return empty;
  const current = studyWindow(entries, today, days);
  const previousEnd = shiftDate(today, -days);
  const previous = studyWindow(entries, previousEnd, days);
  if (current.recordedDays !== days || previous.recordedDays !== days) return empty;
  const start = shiftDate(today, 1 - 2 * days);
  const rows = entries.filter((row) => row.date >= start && row.date <= today);
  const percentage = (metric, total) => {
    if (rows.some((row) => row[metric] == null || row[metric] === '' || !Number.isFinite(Number(row[metric])) || Number(row[metric]) < 0)) return null;
    return previous[total] > 0 ? (current[total] - previous[total]) * 100 / previous[total] : null;
  };
  return { questionsPct: percentage('questions', 'totalQuestions'), pagesPct: percentage('pages', 'totalPages'), complete: true };
}

export function studyComparisonText(comparison, days = 7) {
  const parts = [];
  for (const [key, label] of [['questionsPct', 'Ders soruları'], ['pagesPct', 'Sayfa']]) {
    if (comparison[key] != null) {
      const percentage = Math.round(comparison[key]);
      parts.push(`${label} ${percentage > 0 ? '+' : ''}${percentage}%`);
    }
  }
  return parts.length ? `Son ${days} gün / önceki ${days} gün: ${parts.join(', ')}.`
    : comparison.complete ? 'Karşılaştırılabilir çalışma verisi yok; değişim karşılaştırması gösterilmiyor.'
      : 'Kısmi dönem: değişim karşılaştırması gösterilmiyor.';
}

export function growthMetrics(context, entries, books, stars) {
  if (!context) return [];
  const { today, grade, term } = context;
  if (grade >= 4) {
    const rows = entries?.filter((entry) => entry.date >= context.week_start && entry.date <= today);
    return [
      { label: 'Bu hafta çözülen soru', value: rows ? rows.reduce((sum, row) => sum + Number(row.questions || 0), 0) : '—' },
      { label: 'Bu hafta okunan sayfa', value: rows ? rows.reduce((sum, row) => sum + Number(row.pages || 0), 0) : '—' },
      { label: 'Bu hafta kayıt girilen gün', value: rows ? new Set(rows.map((row) => row.date)).size : '—' },
    ];
  }
  if (!grade) return ['Katılım', 'Kitap', 'Okuma'].map((label) => ({ label, value: 'Bilgi şu anda alınamıyor' }));
  const monthlyBooks = books?.filter((book) => book.date >= `${today.slice(0, 7)}-01` && book.date <= today).length;
  const readingLabels = { completed: 'Tamamlandı', pending: 'Bekliyor', not_assigned: 'Bu hafta okuma atanmadı', not_applicable: 'Henüz kapsam dışı', unknown: 'Bilgi şu anda alınamıyor' };
  return [
    { label: 'Bu ay katılım günü', value: context.monthlyActiveDays ?? '—' },
    { label: 'Bu ay bitirilen kitap', value: monthlyBooks ?? '—' },
    grade === 1 && term === 1
      ? { label: 'Yıldız Bahçem (Güzel Davranış)', value: stars?.total ?? '—' }
      : { label: 'Bu haftanın okuma durumu', value: readingLabels[context.weekly_reading?.state] || 'Bilgi şu anda alınamıyor' },
  ];
}

export function todaySuggestions({ context, entries, books, pendingBooks, homeworks }) {
  if (!context) return { items: [], complete: false };
  const { today } = context;
  const items = [];
  const due = homeworks?.filter((homework) => ['missing', 'not_done'].includes(homework.status) && [today, shiftDate(today, 1)].includes(homework.due_date))
    .sort((left, right) => left.due_date.localeCompare(right.due_date));
  if (due?.length) items.push({ key: 'homework', text: due.length === 1 ? `${due[0].subject_label || 'Ders'} ödevini birlikte kontrol edebilirsiniz.` : `${due.length} ödevi birlikte kontrol edebilirsiniz.`, label: 'Ödevi gör', panel: 'homeworks', homeworkId: due[0].homework_id });
  if (context.weekly_reading?.state === 'pending') items.push({ key: 'reading', text: 'Haftanın okuması sizi bekliyor.', label: 'Okumaya başla', action: 'reading', panel: 'books' });
  const hour = Number(new Intl.DateTimeFormat('en-GB', { timeZone: TURKEY_TIME_ZONE, hour: '2-digit', hourCycle: 'h23' }).format(new Date(context.as_of)));
  if (context.can_write_study && hour >= 17 && entries && !entries.some((entry) => entry.date === today)) items.push({ key: 'study', text: 'Bugünkü çalışmanızı kaydedelim mi?', label: 'Çalışma kaydı ekle', action: 'study' });
  const weekday = new Date(`${today}T12:00:00Z`).getUTCDay();
  if (context.can_write_books && [0, 6].includes(weekday) && books && pendingBooks && ![...books, ...pendingBooks.map((book) => ({ date: book.finished_date }))].some((book) => book.date >= context.week_start && book.date <= today)) items.push({ key: 'book', text: 'Bu hafta bir kitap bitirdiniz mi?', label: 'Kitap ekle', action: 'book' });
  const unknownHomework = homeworks?.some((homework) => [today, shiftDate(today, 1)].includes(homework.due_date) && !['done', 'missing', 'not_done'].includes(homework.status));
  return { items, complete: !unknownHomework && !!entries && !!books && !!pendingBooks && !!homeworks && !['unknown'].includes(context.weekly_reading?.state) };
}


export function nextDashboardRefresh(asOf) {
  const reference = new Date(asOf);
  const today = turkeyDate(reference);
  const afternoon = new Date(`${today}T17:00:00+03:00`);
  return reference < afternoon ? afternoon : new Date(`${shiftDate(today, 1)}T00:00:00+03:00`);
}
